import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, effect, inject, viewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MiscService } from '../../services/misc/misc.service';
import { ValueFirstOnboardingService } from './services/value-first-onboarding.service';
import { DisplayCurrency } from './services/value-first-onboarding.types';
import { VfPathStep } from './molecules/path-step/path-step';
import { VfWebsiteStep } from './molecules/website-step/website-step';
import { VfManualStep } from './molecules/manual-step/manual-step';
import { VfFoundStep } from './molecules/found-step/found-step';
import { VfEmailStep } from './molecules/email-step/email-step';
import { VfCodeStep } from './molecules/code-step/code-step';
import { VfAccountStep } from './molecules/account-step/account-step';
import { VfWelcomeStep } from './molecules/welcome-step/welcome-step';
import { VfWorkspacePanel } from './molecules/workspace-panel/workspace-panel';

const DISPLAY_CURRENCY_KEY = 'onboarding-display-currency';

@Component({
  selector: 'app-onboarding-value-first',
  imports: [
    VfPathStep,
    VfWebsiteStep,
    VfManualStep,
    VfFoundStep,
    VfEmailStep,
    VfCodeStep,
    VfAccountStep,
    VfWelcomeStep,
    VfWorkspacePanel,
  ],
  templateUrl: './onboarding-value-first.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass()' },
})
export class OnboardingValueFirst implements OnInit, OnDestroy {
  readonly onboarding = inject(ValueFirstOnboardingService);
  private readonly route = inject(ActivatedRoute);
  private readonly misc = inject(MiscService);
  private readonly document = inject(DOCUMENT);
  private readonly subscriptionList = new Subscription();
  private readonly stepScroller = viewChild<ElementRef<HTMLElement>>('stepScroller');

  constructor() {
    effect(() => {
      this.onboarding.step();
      this.resetScroll();
    });
  }

  ngOnInit(): void {
    const step = this.route.snapshot.queryParamMap.get('step');
    const path = this.route.snapshot.queryParamMap.get('path');
    if (step === 'website' && path === 'brand') {
      this.onboarding.startAdEntry();
    } else {
      this.onboarding.restore();
    }
    this.resetScroll();
    this.loadDisplayCurrency();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  hostClass(): string {
    return 'flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground';
  }

  frameClass(): string {
    return 'flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground lg:flex-row';
  }

  columnClass(): string {
    return 'flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden lg:w-1/2';
  }

  mainClass(): string {
    return 'mx-auto min-h-0 w-full max-w-lg flex-1 overflow-y-auto px-5 py-8 pb-40 sm:px-8 lg:py-16 lg:pb-12';
  }

  goBack(): void {
    this.onboarding.goBack();
    this.resetScroll();
  }

  private resetScroll(): void {
    this.document.defaultView?.scrollTo({ top: 0, left: 0 });
    this.stepScroller()?.nativeElement.scrollTo({ top: 0, left: 0 });
  }

  private loadDisplayCurrency(): void {
    const cached = this.readCachedCurrency();
    if (cached) {
      this.onboarding.setDisplayCurrency(cached);
    }
    this.subscriptionList.add(
      this.misc.getCountry().subscribe({
        next: (country) => {
          const next: DisplayCurrency = country === 'GB' ? 'GBP' : 'USD';
          this.onboarding.setDisplayCurrency(next);
          this.writeCachedCurrency(next);
        },
      }),
    );
  }

  private readCachedCurrency(): DisplayCurrency | null {
    try {
      const cached = this.document.defaultView?.localStorage.getItem(DISPLAY_CURRENCY_KEY);
      if (cached === 'GBP' || cached === 'USD') {
        return cached;
      }
    } catch {
      return null;
    }
    return null;
  }

  private writeCachedCurrency(currency: DisplayCurrency): void {
    try {
      this.document.defaultView?.localStorage.setItem(DISPLAY_CURRENCY_KEY, currency);
    } catch {
      // Ignore private-mode storage failures.
    }
  }
}
